import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { DimensionNumberInput } from 'ui-components/components/dimension-input/dimension-number-input';
import { messages } from 'ui-components/components/dimension-input/messages';
import { DimensionInputUnit } from 'ui-components/components/dimension-input/units';
import { emptyObject } from 'ui-components/default-props/empty';

import { DimensionType } from '../../../enums/dimension-type';
import { UnitPosition } from '../../../enums/units/unit-position';
import { useFulfillmentPlannerContext } from '../../../fulfillment-planner/context';
import { isCentimeters } from '../../../services/unit-services/dimensions/dimension-type-service';
import smartFillStyle from '../smartfill.module.less';

import style from 'ui-components/components/dimension-input/style.module.less';

const dimensionKeys = Object.values(DimensionType);

/**
 * @typedef {import('../../../enums/units/unit-display-types.d.ts').UnitPosition} UnitPositionType
 */

/**
 * @typedef {import('../../../prop-types/dimension-types.d.ts').Dimensions} Dimensions
 */

/**
 * @param {object} props
 * @param {string | null} [props.className]
 * @param {(inputDimensions: Dimensions) => void} props.onChange
 * @param {string} [props.unit]
 * @param {Dimensions} [props.dimensions]
 * @param {string | null} [props.inputClassName]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.shrinkable]
 * @param {UnitPositionType} [props.unitPosition]
 * @param {string} [props.numberInputsContainerClassName]
 * @param {string} [props.labelId]
 * @param {boolean} [props.shouldBlockUpdateIfFocused]
 * @returns {React.JSX.Element}
 */
export const DimensionInput = ({
  className = null,
  onChange,
  unit = DimensionInputUnit.inches,
  dimensions = emptyObject,
  inputClassName = null,
  disabled = false,
  shrinkable = false,
  unitPosition = UnitPosition.OutsideEnd,
  numberInputsContainerClassName,
  labelId,
  shouldBlockUpdateIfFocused = false,
}) => {
  const [internalDimensions, setInternalDimensions] = useState(dimensions || {});
  const [hasFocus, setHasFocus] = useState(false);
  const { hasSmartFillDataBeenChanged, isSmartFillEnabled } = useFulfillmentPlannerContext();

  const isFocusedRef = useRef(false);

  // I'm not thrilled about this, but the previous class component used getDerivedStateFromProps
  // so this mimics that without modifying behavior.
  useEffect(() => {
    if (shouldBlockUpdateIfFocused && isFocusedRef.current) {
      return;
    }
    setInternalDimensions({ ...dimensions, unit });
  }, [dimensions, unit, shouldBlockUpdateIfFocused]);

  const onDimensionChange = useCallback(
    (dimension, value) => {
      const newDimensions = {
        ...internalDimensions,
        unit,
        [dimension]: value,
      };

      if (hasFocus) {
        setInternalDimensions(newDimensions);
      } else {
        onChange(newDimensions, dimension);
      }
    },
    [internalDimensions, hasFocus, onChange, unit],
  );

  const onBlur = useCallback(
    (dimension) => {
      onChange(
        {
          ...internalDimensions,
          unit,
        },
        dimension,
      );
      isFocusedRef.current = false;
      setHasFocus(false);
    },
    [internalDimensions, onChange, unit],
  );

  const unitFormattedMessage = useMemo(() => {
    return isCentimeters(unit) ? <FormattedMessage {...messages.cm} /> : <FormattedMessage {...messages.in} />;
  }, [unit]);

  const onFocus = useCallback(() => {
    isFocusedRef.current = true;
    setHasFocus(true);
  }, []);

  const shouldHighlightSmartFill = useCallback(
    (dimensionType) => isSmartFillEnabled && !hasSmartFillDataBeenChanged[dimensionType],
    [isSmartFillEnabled, hasSmartFillDataBeenChanged],
  );

  return (
    <div className={classnames(className, style['dimensions-and-unit'])}>
      <div className={classnames(style.dimensions, numberInputsContainerClassName)}>
        {dimensionKeys.map((dimensionType) => (
          <DimensionNumberInput
            key={dimensionType}
            className={classnames(inputClassName, {
              [smartFillStyle['smart-fill-container']]: shouldHighlightSmartFill(dimensionType),
            })}
            dimensionType={dimensionType}
            disabled={disabled}
            labelId={dimensionType}
            onBlur={onBlur}
            onChange={onDimensionChange}
            onFocus={onFocus}
            parentLabelId={labelId}
            shrinkable={shrinkable}
            testId={`${dimensionType}-input`}
            unitPosition={unitPosition}
            value={internalDimensions[dimensionType]}
          />
        ))}
      </div>
      {unitPosition === UnitPosition.OutsideEnd && <label id={labelId}>({unitFormattedMessage})</label>}
    </div>
  );
};
